import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EmptyState, LoadMore, PageContainer, PageHeader } from '../../components';
import { formatDateTime } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { fetchWorkspaceActivity } from './api';
import { activityQueryKeys } from './queryKeys';
import { formatActivity } from './formatActivity';
import type { ActivityRow } from './types';

/**
 * The audit trail — everything that has happened in this workspace, in order.
 *
 * The record has been written since the first migration: every field edit,
 * every board move, every sprint decision goes into `request_activities`, and
 * the endpoint behind it has existed as long as HomePage's twenty-row "Recent
 * activity" card. Twenty rows with no way to go further is a glance, not a
 * record — you cannot answer "who deleted #3666, and when" from it, which is
 * the question an audit trail exists to answer and the question the
 * irreversible actions (delete, merge, unlock a sprint) create.
 *
 * So this is the same feed, paged to the end, with the actions that cannot be
 * undone separable from the ordinary noise of people editing fields.
 *
 * The server applies the client/internal split, including re-checking the
 * parent request's CURRENT `visible_to_client` rather than trusting the
 * activity row's own flag (see activity.service.ts's header). Nothing here
 * re-filters what comes back — the filter below is about what you want to
 * read, never about what you are allowed to see.
 */

const PAGE_SIZE = 50;

/**
 * The kinds worth separating out.
 *
 * Not "destructive" in a technical sense — a board move destroys nothing — but
 * the set of things that, when someone asks "what happened to it", are the
 * answer. Everything here either removes something, ends something, or
 * rewrites a commitment that a client was told about.
 */
const CONSEQUENTIAL = new Set([
  'request.deleted',
  'request.merged_duplicate',
  'request.duplicate_merged',
  'sprint.deleted',
  'sprint.unlocked',
  'sprint.carried_over',
  'sprint.plan_change_agreed',
  'sprint.plan_change_declined',
  'config.changed',
]);

type Filter = 'all' | 'consequential';

export function AuditTrailPage() {
  const { workspace } = useRequiredWorkspace();
  const [filter, setFilter] = useState<Filter>('all');

  const query = useInfiniteQuery({
    queryKey: [...activityQueryKeys.workspace(workspace.id), 'audit'],
    queryFn: ({ pageParam, signal }) =>
      fetchWorkspaceActivity(workspace.id, { limit: PAGE_SIZE, cursor: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const rows = useMemo(() => (query.data?.pages ?? []).flatMap((page) => page.items), [query.data]);

  // Filtered in the browser, not by the server, and that is a real limitation
  // rather than a shortcut: with the filter on, a page of 50 rows may contain
  // two, so "Load more" is loading more of the underlying feed rather than
  // fifty more matches. Said plainly under the list rather than left to be
  // discovered. A server-side `kind` filter is the fix when this list grows
  // past the point where paging through it is reasonable.
  const visible = filter === 'all' ? rows : rows.filter((row) => CONSEQUENTIAL.has(row.kind));

  return (
    <PageContainer>
      <PageHeader
        title="Audit trail"
        description="Everything that has happened in this workspace, newest first — who changed what, and when."
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        {(
          [
            ['all', 'Everything'],
            ['consequential', 'Deletions, merges and plan changes'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
            className={cx(
              'rounded-full border px-3 py-1 fs-12 font-semibold',
              filter === value
                ? 'border-ink bg-ink text-white'
                : 'border-line bg-base text-muted hover:border-line-stronger hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {query.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : query.error !== null ? (
        <EmptyState title="Could not load the audit trail" description={query.error.message} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={filter === 'all' ? 'Nothing recorded yet' : 'Nothing of that kind yet'}
          description={
            filter === 'all'
              ? 'Every change in this workspace is recorded here as it happens.'
              : 'No deletions, merges or agreed plan changes have been recorded.'
          }
        />
      ) : (
        <>
          <ol className="overflow-hidden rounded-xl border border-line">
            {visible.map((row) => (
              <li key={row.id} className="border-b border-surface-3 last:border-b-0">
                <AuditLine row={row} />
              </li>
            ))}
          </ol>

          <div className="mt-3 flex flex-col gap-2">
            <LoadMore
              hasMore={query.hasNextPage}
              isLoading={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
              label="Load older activity"
            />
            {filter !== 'all' && (
              <p className="fs-11 text-faint">
                Showing {visible.length} of {rows.length} loaded entries. Loading more loads more of the whole
                trail, not only matches.
              </p>
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
}

function AuditLine({ row }: { row: ActivityRow }) {
  const consequential = CONSEQUENTIAL.has(row.kind);

  const body = (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-4 py-2.5">
      <span className="numeric w-[9.5rem] flex-none fs-11 text-faint">{formatDateTime(row.createdAt)}</span>
      {row.requestNumber !== null && (
        <span className="numeric flex-none fs-12 text-muted">#{row.requestNumber}</span>
      )}
      <span
        className={cx('min-w-0 flex-1 fs-12.5', consequential ? 'font-medium text-ink' : 'text-ink-soft')}
      >
        {formatActivity(row)}
      </span>
    </div>
  );

  // A deleted request has no page left to open, and its row carries
  // `requestId: null` precisely so the record outlives the thing it records
  // (see RequestsService.remove). So: link only what still exists.
  return row.requestId !== null ? (
    <Link to={`/requests/${row.requestId}`} className="block hover:bg-surface">
      {body}
    </Link>
  ) : (
    body
  );
}
